import { refreshAdmin } from './admin-auth-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import { normalizeWechatPermanentEntryUrl } from './wechat-permanent-entry-url';

export interface AdminWechatPermanentEntryIssueCounts {
  total: number;
  pending: number;
  confirmed: number;
  expired: number;
  revoked: number;
}

export interface AdminWechatPermanentEntry {
  id: string;
  publicToken: string;
  publicUrl: string;
  avatarKey: string;
  profile: string;
  displayName: string;
  agentIds: string[];
  defaultAgentId: string;
  status: string;
  expiresAt: string | null;
}

export interface AdminWechatPermanentEntrySummary {
  totalEntries: number;
  activeEntries: number;
  disabledEntries: number;
  expiredEntries: number;
  pendingIssues: number;
  confirmedIssues24h: number;
  expiredIssues24h: number;
  confirmationRate24h: number;
}

export interface AdminWechatPermanentEntryListItem {
  id: string;
  publicToken: string;
  publicUrl: string;
  avatarKey: string;
  profile: string;
  displayName: string;
  agentIds: string[];
  defaultAgentId: string;
  status: string;
  effectiveStatus: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  canIssue: boolean;
  latestIssueStatus: string | null;
  lastConfirmedAt: string | null;
  issueCounts: AdminWechatPermanentEntryIssueCounts;
}

export interface AdminWechatPermanentEntryListResponse {
  summary: AdminWechatPermanentEntrySummary;
  items: AdminWechatPermanentEntryListItem[];
}

export interface AdminWechatPermanentEntryDetail extends AdminWechatPermanentEntryListItem {
  lastIssueAt: string | null;
  totalLinkCount: number;
  currentLinkCount: number;
}

export interface AdminWechatPermanentEntryActivityIssue {
  issueId: string;
  viewerToken: string;
  status: string;
  qrcode: string;
  scanLink: string;
  createdAt: string;
  expiresAt: string;
  confirmedAt: string | null;
  bindingSenderId: string | null;
  confirmedIlinkBotId: string | null;
}

export interface AdminWechatPermanentEntryActivityLink {
  id: string;
  userId: string;
  isCurrent: boolean;
  firstConnectedAt: string;
  lastConnectedAt: string;
}

export interface AdminWechatPermanentEntryActivity {
  issues: AdminWechatPermanentEntryActivityIssue[];
  recentLinks: AdminWechatPermanentEntryActivityLink[];
}

export interface CreateAdminWechatPermanentEntryInput {
  avatarKey: string;
  profile: string;
  displayName: string;
  agentIds: string[];
  defaultAgentId: string;
  expiresAt: string | null;
}

interface ApiIssueCountsResponse {
  total: number;
  pending: number;
  confirmed: number;
  expired: number;
  revoked: number;
}

interface ApiSummaryResponse {
  total_entries: number;
  active_entries: number;
  disabled_entries: number;
  expired_entries: number;
  pending_issues: number;
  confirmed_issues_24h: number;
  expired_issues_24h: number;
  confirmation_rate_24h: number;
}

interface ApiListItemResponse {
  id: string;
  public_token: string;
  public_url: string;
  avatar_key: string;
  profile: string;
  display_name: string;
  agent_ids: string[];
  default_agent_id: string;
  status: string;
  effective_status: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  can_issue: boolean;
  latest_issue_status: string | null;
  last_confirmed_at: string | null;
  issue_counts: ApiIssueCountsResponse;
}

interface ApiListResponse {
  summary: ApiSummaryResponse;
  items: ApiListItemResponse[];
}

interface ApiDetailResponse extends ApiListItemResponse {
  last_issue_at: string | null;
  total_link_count: number;
  current_link_count: number;
}

interface ApiActivityIssueResponse {
  issue_id: string;
  viewer_token: string;
  status: string;
  qrcode: string;
  scan_link: string;
  created_at: string;
  expires_at: string;
  confirmed_at: string | null;
  binding_sender_id: string | null;
  confirmed_ilink_bot_id: string | null;
}

interface ApiActivityLinkResponse {
  id: string;
  user_id: string;
  is_current: boolean;
  first_connected_at: string;
  last_connected_at: string;
}

interface ApiActivityResponse {
  issues: ApiActivityIssueResponse[];
  recent_links: ApiActivityLinkResponse[];
}

interface ApiEntryResponse {
  id: string;
  public_token: string;
  public_url: string;
  avatar_key: string;
  profile: string;
  display_name: string;
  agent_ids: string[];
  default_agent_id: string;
  status: string;
  expires_at: string | null;
}

let adminSessionRefreshPromise: Promise<string | null> | null = null;

async function readErrorMessage(response: Response): Promise<string> {
  const fallback = response.statusText || 'Request failed.';
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail;
      }
      if (payload.detail != null) {
        return JSON.stringify(payload.detail);
      }
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  const text = await response.text().catch(() => fallback);
  return text || fallback;
}

async function refreshAdminAccessToken(): Promise<string | null> {
  if (adminSessionRefreshPromise) {
    return adminSessionRefreshPromise;
  }

  adminSessionRefreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => {
      adminSessionRefreshPromise = null;
    });

  return adminSessionRefreshPromise;
}

async function requestAdminWechatPermanentEntry<T>(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<T> {
  const sendRequest = (token: string) =>
    fetch(input, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });

  let response = await sendRequest(accessToken);

  if (response.status === 401) {
    const refreshedAccessToken = await refreshAdminAccessToken();
    if (refreshedAccessToken) {
      response = await sendRequest(refreshedAccessToken);
    }
  }

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  return response.json() as Promise<T>;
}

function normalizeIssueCounts(payload: ApiIssueCountsResponse): AdminWechatPermanentEntryIssueCounts {
  return { ...payload };
}

function normalizeSummary(payload: ApiSummaryResponse): AdminWechatPermanentEntrySummary {
  return {
    totalEntries: payload.total_entries,
    activeEntries: payload.active_entries,
    disabledEntries: payload.disabled_entries,
    expiredEntries: payload.expired_entries,
    pendingIssues: payload.pending_issues,
    confirmedIssues24h: payload.confirmed_issues_24h,
    expiredIssues24h: payload.expired_issues_24h,
    confirmationRate24h: payload.confirmation_rate_24h,
  };
}

function normalizeListItem(payload: ApiListItemResponse): AdminWechatPermanentEntryListItem {
  return {
    id: payload.id,
    publicToken: payload.public_token,
    publicUrl: normalizeWechatPermanentEntryUrl(payload.public_token, payload.public_url),
    avatarKey: payload.avatar_key,
    profile: payload.profile,
    displayName: payload.display_name,
    agentIds: payload.agent_ids,
    defaultAgentId: payload.default_agent_id,
    status: payload.status,
    effectiveStatus: payload.effective_status,
    expiresAt: payload.expires_at,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
    canIssue: payload.can_issue,
    latestIssueStatus: payload.latest_issue_status,
    lastConfirmedAt: payload.last_confirmed_at,
    issueCounts: normalizeIssueCounts(payload.issue_counts),
  };
}

function normalizeDetail(payload: ApiDetailResponse): AdminWechatPermanentEntryDetail {
  return {
    ...normalizeListItem(payload),
    lastIssueAt: payload.last_issue_at,
    totalLinkCount: payload.total_link_count,
    currentLinkCount: payload.current_link_count,
  };
}

function normalizeEntry(payload: ApiEntryResponse): AdminWechatPermanentEntry {
  return {
    id: payload.id,
    publicToken: payload.public_token,
    publicUrl: normalizeWechatPermanentEntryUrl(payload.public_token, payload.public_url),
    avatarKey: payload.avatar_key,
    profile: payload.profile,
    displayName: payload.display_name,
    agentIds: payload.agent_ids,
    defaultAgentId: payload.default_agent_id,
    status: payload.status,
    expiresAt: payload.expires_at,
  };
}

export async function listAdminWechatPermanentEntries(
  accessToken: string,
  options: { q?: string; status?: string; limit?: number } = {},
): Promise<AdminWechatPermanentEntryListResponse> {
  const params = new URLSearchParams();
  if (options.q?.trim()) {
    params.set('q', options.q.trim());
  }
  if (options.status?.trim()) {
    params.set('status', options.status.trim());
  }
  if (options.limit != null) {
    params.set('limit', String(options.limit));
  }

  const path = params.size > 0
    ? `/api/v1/wechat/permanent-entries?${params.toString()}`
    : '/api/v1/wechat/permanent-entries';
  const payload = await requestAdminWechatPermanentEntry<ApiListResponse>(
    accessToken,
    path,
    { method: 'GET' },
    'Load permanent entries',
  );

  return {
    summary: normalizeSummary(payload.summary),
    items: payload.items.map(normalizeListItem),
  };
}

export async function getAdminWechatPermanentEntry(
  accessToken: string,
  entryId: string,
): Promise<AdminWechatPermanentEntryDetail> {
  const payload = await requestAdminWechatPermanentEntry<ApiDetailResponse>(
    accessToken,
    `/api/v1/wechat/permanent-entries/${entryId}`,
    { method: 'GET' },
    'Load permanent entry detail',
  );
  return normalizeDetail(payload);
}

export async function getAdminWechatPermanentEntryActivity(
  accessToken: string,
  entryId: string,
  limit = 12,
): Promise<AdminWechatPermanentEntryActivity> {
  const payload = await requestAdminWechatPermanentEntry<ApiActivityResponse>(
    accessToken,
    `/api/v1/wechat/permanent-entries/${entryId}/activity?limit=${limit}`,
    { method: 'GET' },
    'Load permanent entry activity',
  );

  return {
    issues: payload.issues.map((issue) => ({
      issueId: issue.issue_id,
      viewerToken: issue.viewer_token,
      status: issue.status,
      qrcode: issue.qrcode,
      scanLink: issue.scan_link,
      createdAt: issue.created_at,
      expiresAt: issue.expires_at,
      confirmedAt: issue.confirmed_at,
      bindingSenderId: issue.binding_sender_id,
      confirmedIlinkBotId: issue.confirmed_ilink_bot_id,
    })),
    recentLinks: payload.recent_links.map((link) => ({
      id: link.id,
      userId: link.user_id,
      isCurrent: link.is_current,
      firstConnectedAt: link.first_connected_at,
      lastConnectedAt: link.last_connected_at,
    })),
  };
}

export async function createAdminWechatPermanentEntry(
  accessToken: string,
  input: CreateAdminWechatPermanentEntryInput,
): Promise<AdminWechatPermanentEntry> {
  const payload = await requestAdminWechatPermanentEntry<ApiEntryResponse>(
    accessToken,
    '/api/v1/wechat/permanent-entries',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        avatar_key: input.avatarKey,
        profile: input.profile,
        display_name: input.displayName,
        agent_ids: input.agentIds,
        default_agent_id: input.defaultAgentId,
        expires_at: input.expiresAt,
      }),
    },
    'Create permanent entry',
  );
  return normalizeEntry(payload);
}

export async function disableAdminWechatPermanentEntry(
  accessToken: string,
  entryId: string,
): Promise<AdminWechatPermanentEntry> {
  const payload = await requestAdminWechatPermanentEntry<ApiEntryResponse>(
    accessToken,
    `/api/v1/wechat/permanent-entries/${entryId}/disable`,
    { method: 'POST' },
    'Disable permanent entry',
  );
  return normalizeEntry(payload);
}
